"use client";

import { useState } from "react";
import Link from "next/link";
import {
  GraduationCap,
  ArrowRight,
  AlertTriangle,
  Target,
  Lightbulb,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DiseaseItem {
  slug: string;
  subspecialty: string;
  title: string;
  category?: string;
  difficulty?: string;
  emergency?: boolean;
  threeLineSummary?: {
    core?: string;
    redFlag?: string;
    treatment?: string;
  };
}

interface SubspecialtyGroup {
  subId: string;
  subName: string;
  items: DiseaseItem[];
}

interface Props {
  totalCount: number;
  coreCount: number;
  advancedCount: number;
  subspecialties: { id: string; name: string; count: number }[];
  grouped: SubspecialtyGroup[];
}

function DifficultyBadge({ difficulty }: { difficulty?: string }) {
  if (!difficulty) return null;
  const config: Record<string, { label: string; className: string }> = {
    core: {
      label: "Core",
      className: "bg-clinical-green/15 text-clinical-green border-clinical-green/30",
    },
    advanced: {
      label: "Advanced",
      className: "bg-clinical-blue/15 text-clinical-blue border-clinical-blue/30",
    },
    rare: {
      label: "Rare",
      className: "bg-clinical-purple/15 text-clinical-purple border-clinical-purple/30",
    },
  };
  const c = config[difficulty];
  if (!c) return null;
  return (
    <Badge variant="outline" className={c.className}>
      {c.label}
    </Badge>
  );
}

export function BoardReviewClient({ totalCount, coreCount, advancedCount, subspecialties, grouped }: Props) {
  const [filter, setFilter] = useState<string | null>(null);
  const [diffFilter, setDiffFilter] = useState<string | null>(null);

  const applyDiffFilter = (groups: SubspecialtyGroup[]) => {
    if (!diffFilter) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((d) => {
          if (diffFilter === "high-yield") return true;
          return d.difficulty === diffFilter;
        }),
      }))
      .filter((g) => g.items.length > 0);
  };

  const filteredGroups = applyDiffFilter(
    filter ? grouped.filter((g) => g.subId === filter) : grouped
  );

  const filteredCount = filteredGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-clinical-amber/15">
          <GraduationCap className="h-6 w-6 text-clinical-amber" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Board Review</h1>
          <p className="text-sm text-muted-foreground">
            High-yield 포인트 — {filteredCount}개 질환
            {(filter || diffFilter) && ` (필터 적용 중)`}
          </p>
        </div>
      </div>

      {/* Stats - clickable filters */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setDiffFilter(diffFilter === "high-yield" ? null : "high-yield")}
          className={`rounded-lg border p-3 text-center transition-all cursor-pointer ${
            diffFilter === "high-yield" || !diffFilter
              ? "border-clinical-amber/20 bg-clinical-amber/5"
              : "border-border bg-card opacity-50"
          } ${diffFilter === "high-yield" ? "ring-2 ring-clinical-amber/40" : ""} hover:shadow-md hover:-translate-y-0.5`}
        >
          <p className="text-2xl font-bold text-clinical-amber">{totalCount}</p>
          <p className="text-xs text-muted-foreground">High-Yield 질환</p>
        </button>
        <button
          onClick={() => setDiffFilter(diffFilter === "core" ? null : "core")}
          className={`rounded-lg border p-3 text-center transition-all cursor-pointer ${
            diffFilter === "core" || !diffFilter
              ? "border-clinical-green/20 bg-clinical-green/5"
              : "border-border bg-card opacity-50"
          } ${diffFilter === "core" ? "ring-2 ring-clinical-green/40" : ""} hover:shadow-md hover:-translate-y-0.5`}
        >
          <p className="text-2xl font-bold text-clinical-green">{coreCount}</p>
          <p className="text-xs text-muted-foreground">Core 난이도</p>
        </button>
        <button
          onClick={() => setDiffFilter(diffFilter === "advanced" ? null : "advanced")}
          className={`rounded-lg border p-3 text-center transition-all cursor-pointer ${
            diffFilter === "advanced" || !diffFilter
              ? "border-clinical-blue/20 bg-clinical-blue/5"
              : "border-border bg-card opacity-50"
          } ${diffFilter === "advanced" ? "ring-2 ring-clinical-blue/40" : ""} hover:shadow-md hover:-translate-y-0.5`}
        >
          <p className="text-2xl font-bold text-clinical-blue">{advancedCount}</p>
          <p className="text-xs text-muted-foreground">Advanced 난이도</p>
        </button>
      </div>

      {/* Subspecialty filter chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(null)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            !filter
              ? "bg-clinical-amber text-white border-clinical-amber"
              : "hover:bg-accent"
          }`}
        >
          전체 ({totalCount})
        </button>
        {subspecialties.map((sub) => (
          <button
            key={sub.id}
            onClick={() => setFilter(filter === sub.id ? null : sub.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              filter === sub.id
                ? "bg-clinical-amber text-white border-clinical-amber"
                : "hover:bg-accent"
            }`}
          >
            {sub.name} ({sub.count})
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">난이도:</span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-clinical-green" />
          Core
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-clinical-blue" />
          Advanced
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-clinical-purple" />
          Rare
        </span>
        <span className="ml-2 font-medium text-foreground">표시:</span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-clinical-red" />
          응급
        </span>
      </div>

      {totalCount === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-muted-foreground">
          High-yield 콘텐츠가 아직 없습니다.
        </div>
      ) : (
        filteredGroups.map(({ subId, subName, items }) => (
          <section key={subId} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{subName}</h2>
              <Badge variant="outline" className="text-xs">
                {items.length}개
              </Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((d) => {
                const tls = d.threeLineSummary;
                return (
                  <div
                    key={d.slug}
                    className="rounded-xl border border-border bg-card shadow-sm overflow-hidden hover:border-clinical-amber/30 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 border-b border-border bg-clinical-amber/5 p-4">
                      <Link
                        href={`/${d.subspecialty}/${d.slug}`}
                        className="group flex-1"
                      >
                        <h3 className="font-semibold group-hover:text-clinical-amber transition-colors">
                          {d.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {d.category}
                        </p>
                      </Link>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {d.emergency && (
                          <AlertTriangle className="h-3.5 w-3.5 text-clinical-red" />
                        )}
                        <DifficultyBadge difficulty={d.difficulty} />
                      </div>
                    </div>

                    {tls && (
                      <div className="p-4 space-y-2.5">
                        {tls.core && (
                          <div className="flex gap-2 text-xs">
                            <Target className="h-3.5 w-3.5 mt-0.5 shrink-0 text-clinical-blue" />
                            <p className="text-muted-foreground leading-relaxed">
                              <span className="font-medium text-foreground">핵심: </span>
                              {tls.core}
                            </p>
                          </div>
                        )}
                        {tls.redFlag && (
                          <div className="flex gap-2 text-xs">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-clinical-red" />
                            <p className="text-muted-foreground leading-relaxed">
                              <span className="font-medium text-foreground">주의: </span>
                              {tls.redFlag}
                            </p>
                          </div>
                        )}
                        {tls.treatment && (
                          <div className="flex gap-2 text-xs">
                            <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 text-clinical-amber" />
                            <p className="text-muted-foreground leading-relaxed">
                              <span className="font-medium text-foreground">치료: </span>
                              {tls.treatment}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="px-4 pb-3">
                      <Link
                        href={`/${d.subspecialty}/${d.slug}`}
                        className="flex items-center gap-1 text-xs font-medium text-clinical-amber hover:underline"
                      >
                        상세 보기
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
